import {
    LineCapStyle,
    PDFDocument,
    concatTransformationMatrix,
    drawObject,
    lineTo,
    moveTo,
    popGraphicsState,
    pushGraphicsState,
    setDashPattern,
    setLineCap,
    setLineWidth,
    setStrokingGrayscaleColor,
    stroke,
} from 'pdf-lib';
import type { PDFEmbeddedPage, PDFOperator, PDFPage } from 'pdf-lib';

/**
 * Vorankündigung als Aushang auf der Plotter-Rolle.
 *
 * Die A4-Seiten aus `generateVorankuendigungPDF` kommen in ORIGINALGRÖSSE
 * auf EIN Blatt, das so breit ist wie die Rolle (Betreiber 23.10.2026: der
 * erste Stand skalierte auf Rollenbreite hoch — „viel zu groß“). Je Reihe
 * liegen zwei Seiten quer nebeneinander über die Rolle, mittig: 2 × 297 mm
 * auf 610 mm lassen an jeder Rollenkante 8 mm Kleberand. Eine dritte Seite
 * beginnt eine zweite Reihe dahinter — abgeschnitten ergibt das zwei Blätter.
 * Die Seiten bleiben Vektor (Form-XObjects): gerastert würde die Schrift
 * unscharf, und die Datei würde ein Vielfaches größer.
 *
 * Lage auf dem ungedrehten Blatt, so wie es aus dem Plotter läuft: die
 * Oberkante jeder Seite zeigt zur LINKEN Rollenkante, Seite 1 liegt links.
 * Wer das Blatt um 90° im Uhrzeigersinn dreht, hat Seite 1 über Seite 2,
 * beide aufrecht — der Kleberand an den Rollenkanten liegt dann oben und
 * unten. Entlang der Rolle trennt ein Abstand die Reihen und die Blattenden.
 */

/** Seiten je Reihe quer über die Rolle. */
export const VORANK_PLOT_SEITEN_JE_REIHE = 2;
/** Am Blattanfang, zwischen den Reihen und am Blattende. */
export const VORANK_PLOT_ABSTAND_MM = 10;
/**
 * Mindestrand an jeder Rollenkante — darunter druckt der Plotter nicht
 * (Hardware-Rand der Rolle). Der Rand selbst ist, was die Rolle neben den
 * Seiten übrig lässt.
 */
export const VORANK_PLOT_RAND_MIN_MM = 5;
/** Originalgröße: die Seite wird weder vergrößert noch verkleinert. */
const FAKTOR = 1;

/** Grenzen wie in `plot_auftrag_anlegen` (supabase/plot_auftraege_rpc.sql) — darüber lehnt der Server ab. */
const BREITE_MIN_MM = 100;
const BREITE_MAX_MM = 2000;
const LAENGE_MIN_MM = 100;
const LAENGE_MAX_MM = 6000;

const A4_BREITE_MM = 210;
const A4_HOEHE_MM = 297;

/** pt je mm */
const MM = 72 / 25.4;

/**
 * Rahmen um jede Seite, damit der Aushang wie aufgeklebte Einzelblätter
 * wirkt. Mittelgrau und dünn: aus ein paar Metern als Blattkante zu erkennen,
 * ohne mit dem Text zu konkurrieren.
 */
const RAHMEN_GRAU = 0.5;
const RAHMEN_LINIE_MM = 0.4;
const RAHMEN_STRICH_MM = 4;
const RAHMEN_LUECKE_MM = 3;

/** Lage einer Seite auf dem Blatt, in mm, Ursprung LINKS OBEN des Blatts (x nach rechts, y nach unten). Das ist der gestrichelte Rahmen. */
export interface VorankPlotSeite { xMm: number; yMm: number; breiteMm: number; hoeheMm: number }

export interface VorankPlotMasse {
    breiteMm: number;   // = Rollenbreite
    hoeheMm: number;    // Blattlänge, auf 0,1 mm
    faktor: number;     // immer 1 — Originalgröße
    randMm: number;     // Kleberand an jeder Rollenkante
    seiten: VorankPlotSeite[]; // Rahmen der GEDREHTEN Seiten auf dem Blatt (breiteMm = quer zur Rolle = Quellhöhe, hoeheMm = entlang der Rolle = Quellbreite)
}

export interface VorankPlotBlatt { blob: Blob; breiteMm: number; hoeheMm: number; seiten: number }

const KEINE_SEITEN = 'Die Vorankündigung hat keine Seiten — es gibt nichts zu plotten.';

/**
 * Zehntelmillimeter wie die Spalten `breite_mm`/`hoehe_mm` (numeric(7,1)).
 * Über die Exponentenschreibweise statt `x * 10` (wie `runde` in plotRolle.ts),
 * und mit einer Toleranz weit unter jeder Druckgenauigkeit: sonst würde ein
 * Fließkommarest wie 940,3000000000001 auf 940,4 aufgerundet.
 */
const aufZehntel = (mm: number): number => Number(`${Math.ceil(Number(`${mm}e1`) - 1e-6)}e-1`);
const abZehntel = (mm: number): number => Number(`${Math.floor(Number(`${mm}e1`) + 1e-6)}e-1`);

/** „836,1“ für Fehlertexte, ohne von der Locale des Geräts abzuhängen. */
const mmText = (mm: number): string => String(Math.round(mm * 10) / 10).replace('.', ',');

/** Reine Rechnung, ohne PDF. quellBreiteMm/quellHoeheMm: Größe der Quellseite (Standard A4 210 × 297). */
export function vorankPlotMasse(a: { seitenAnzahl: number; rollenBreiteMm: number; quellBreiteMm?: number; quellHoeheMm?: number }): VorankPlotMasse {
    const { seitenAnzahl, rollenBreiteMm, quellBreiteMm = A4_BREITE_MM, quellHoeheMm = A4_HOEHE_MM } = a;

    if (!Number.isInteger(seitenAnzahl) || seitenAnzahl < 1) throw new Error(KEINE_SEITEN);
    if (!(Number.isFinite(quellBreiteMm) && Number.isFinite(quellHoeheMm) && quellBreiteMm > 0 && quellHoeheMm > 0)) {
        throw new Error('Die Seitengröße der Vorankündigung ist ungültig.');
    }
    if (!Number.isFinite(rollenBreiteMm)) throw new Error('Die Rollenbreite fehlt oder ist keine Zahl.');

    // Abrunden, nicht runden: das Blatt darf nie breiter sein als die Rolle.
    // Gerechnet wird mit dem gerundeten Wert, damit PDF und gemeldetes Maß
    // übereinstimmen.
    const breiteMm = abZehntel(rollenBreiteMm);
    if (!(breiteMm >= BREITE_MIN_MM && breiteMm <= BREITE_MAX_MM)) {
        throw new Error(
            `Rollenbreite ${mmText(rollenBreiteMm)} mm: der Plotter nimmt Rollen von ${BREITE_MIN_MM} bis ${BREITE_MAX_MM} mm.`,
        );
    }

    // Gedreht: die Seitenhöhe liegt quer zur Rolle, die Seitenbreite entlang.
    const querMm = quellHoeheMm * FAKTOR;
    const laengsMm = quellBreiteMm * FAKTOR;
    const randMm = (breiteMm - VORANK_PLOT_SEITEN_JE_REIHE * querMm) / 2;
    if (randMm < VORANK_PLOT_RAND_MIN_MM) {
        const noetig = VORANK_PLOT_SEITEN_JE_REIHE * querMm + 2 * VORANK_PLOT_RAND_MIN_MM;
        throw new Error(
            `Die Rolle ist mit ${mmText(rollenBreiteMm)} mm zu schmal: zwei Seiten quer nebeneinander brauchen mindestens ${mmText(noetig)} mm.`,
        );
    }

    const reihen = Math.ceil(seitenAnzahl / VORANK_PLOT_SEITEN_JE_REIHE);
    // Aufrunden: der Rest landet im Abstand am Blattende, der damit nie unter
    // VORANK_PLOT_ABSTAND_MM fällt.
    const hoeheMm = aufZehntel(reihen * laengsMm + (reihen + 1) * VORANK_PLOT_ABSTAND_MM);
    if (hoeheMm > LAENGE_MAX_MM) {
        throw new Error(
            `Das Plot-Blatt wäre ${mmText(hoeheMm)} mm lang; der Plotter nimmt höchstens ${LAENGE_MAX_MM} mm. `
            + `Weniger Seiten verkürzen es.`,
        );
    }
    if (hoeheMm < LAENGE_MIN_MM) {
        throw new Error(
            `Das Plot-Blatt wäre nur ${mmText(hoeheMm)} mm lang; der Plotter braucht mindestens ${LAENGE_MIN_MM} mm.`,
        );
    }

    // Die beiden Seiten einer Reihe stoßen in der Mitte aneinander — 2 × 297
    // auf 610 mm lässt für eine Lücke keinen Platz; die Rahmen teilen sich
    // dort eine Kante.
    const seiten = Array.from({ length: seitenAnzahl }, (_, i): VorankPlotSeite => ({
        xMm: randMm + (i % VORANK_PLOT_SEITEN_JE_REIHE) * querMm,
        yMm: VORANK_PLOT_ABSTAND_MM + Math.floor(i / VORANK_PLOT_SEITEN_JE_REIHE) * (laengsMm + VORANK_PLOT_ABSTAND_MM),
        breiteMm: querMm,
        hoeheMm: laengsMm,
    }));

    return { breiteMm, hoeheMm, faktor: FAKTOR, randMm, seiten };
}

/**
 * Strichmuster für eine Rahmenkante: Anfang und Ende liegen auf einem Strich,
 * damit jede Ecke geschlossen ist und der Rahmen wie eine Blattkante wirkt,
 * statt irgendwo im Muster abzureißen. Dafür wird das Muster je Kante um
 * Bruchteile eines Millimeters gestreckt oder gestaucht.
 *
 * Die Linienenden sind projizierend (sonst fehlt an jeder Ecke ein Quadrat
 * halber Linienbreite); sie verlängern jeden Strich um eine Linienbreite.
 * Das wird hier abgezogen, sichtbar bleiben Strich und Lücke wie benannt.
 */
const strichmuster = (laengeMm: number): [number, number] => {
    const periode = RAHMEN_STRICH_MM + RAHMEN_LUECKE_MM;
    const anzahl = Math.max(1, Math.round((laengeMm - RAHMEN_STRICH_MM) / periode));
    const streckung = (laengeMm + RAHMEN_LINIE_MM) / (anzahl * periode + RAHMEN_STRICH_MM);
    return [
        (RAHMEN_STRICH_MM * streckung - RAHMEN_LINIE_MM) * MM,
        (RAHMEN_LUECKE_MM * streckung + RAHMEN_LINIE_MM) * MM,
    ];
};

/** Eine gestrichelte Kante von (x1|y1) nach (x2|y2), in pt. */
const kante = (x1: number, y1: number, x2: number, y2: number): PDFOperator[] => {
    const laengeMm = Math.hypot(x2 - x1, y2 - y1) / MM;
    return [setDashPattern(strichmuster(laengeMm), 0), moveTo(x1, y1), lineTo(x2, y2), stroke()];
};

const zeichneRahmen = (blatt: PDFPage, seiten: VorankPlotSeite[], blattHoehePt: number) => {
    const ops: PDFOperator[] = [
        pushGraphicsState(),
        setStrokingGrayscaleColor(RAHMEN_GRAU),
        setLineWidth(RAHMEN_LINIE_MM * MM),
        setLineCap(LineCapStyle.Projecting),
    ];
    for (const s of seiten) {
        const links = s.xMm * MM;
        const rechts = (s.xMm + s.breiteMm) * MM;
        const oben = blattHoehePt - s.yMm * MM;
        const unten = blattHoehePt - (s.yMm + s.hoeheMm) * MM;
        ops.push(
            ...kante(links, oben, rechts, oben),
            ...kante(rechts, oben, rechts, unten),
            ...kante(rechts, unten, links, unten),
            ...kante(links, unten, links, oben),
        );
    }
    ops.push(popGraphicsState());
    blatt.pushOperators(...ops);
};

/** Baut das Plot-Blatt aus dem A4-PDF (Bytes von `generateVorankuendigungPDF(data).output('arraybuffer')`). */
export async function erzeugeVorankPlotBlatt(a4Pdf: ArrayBuffer | Uint8Array, rollenBreiteMm: number): Promise<VorankPlotBlatt> {
    if (!a4Pdf || a4Pdf.byteLength === 0) throw new Error('Die Vorankündigung ist leer — es gibt nichts zu plotten.');

    let quelle: PDFDocument;
    try {
        quelle = await PDFDocument.load(a4Pdf, { updateMetadata: false });
    } catch {
        throw new Error('Die Vorankündigung lässt sich nicht als PDF lesen.');
    }
    const seitenAnzahl = quelle.getPageCount();
    if (seitenAnzahl === 0) throw new Error(KEINE_SEITEN);

    const { width, height } = quelle.getPage(0).getSize();
    const masse = vorankPlotMasse({ seitenAnzahl, rollenBreiteMm, quellBreiteMm: width / MM, quellHoeheMm: height / MM });

    // updateMetadata: false — sonst trägt pdf-lib sich als Ersteller und
    // Produzent ein. Der Aushang trägt nur seinen Titel.
    const ziel = await PDFDocument.create({ updateMetadata: false });
    ziel.setTitle('Vorankündigung');

    let eingebettet: PDFEmbeddedPage[];
    try {
        eingebettet = await ziel.embedPdf(quelle, quelle.getPageIndices());
        // pdf-lib bettet sonst erst beim Speichern ein; ein Fehler (etwa eine
        // Seite ganz ohne Inhalt) käme dann englisch aus save().
        await Promise.all(eingebettet.map(e => e.embed()));
    } catch {
        throw new Error('Die Seiten der Vorankündigung lassen sich nicht übernehmen.');
    }
    const blattHoehePt = masse.hoeheMm * MM;
    const blatt = ziel.addPage([masse.breiteMm * MM, blattHoehePt]);
    const f = masse.faktor;

    masse.seiten.forEach((s, i) => {
        const name = blatt.node.newXObject('Seite', eingebettet[i].ref);
        // Eine einzige Matrix statt drawPage mit translate/rotate/scale: so
        // steht die Drehung exakt (ohne cos(90°) ≈ 6e-17) im Content-Stream
        // und lässt sich dort nachprüfen. Quellpunkt (u|v) → (x0 − f·v | y0 + f·u):
        // die Seitenoberkante (v = Höhe) landet an der linken Rahmenkante, die
        // rechte Seitenkante (u = Breite) an der oberen — Ursprung (x0|y0) ist
        // die rechte untere Rahmenecke in PDF-Koordinaten.
        blatt.pushOperators(
            pushGraphicsState(),
            concatTransformationMatrix(0, f, -f, 0, (s.xMm + s.breiteMm) * MM, blattHoehePt - (s.yMm + s.hoeheMm) * MM),
            drawObject(name),
            popGraphicsState(),
        );
    });

    zeichneRahmen(blatt, masse.seiten, blattHoehePt);

    const bytes = await ziel.save();
    return {
        blob: new Blob([bytes], { type: 'application/pdf' }),
        breiteMm: masse.breiteMm,
        hoeheMm: masse.hoeheMm,
        seiten: seitenAnzahl,
    };
}
